import { queryOptions, useQuery } from '@tanstack/react-query'

import { ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { assistantEffects } from '@/lib/assistant-effects.generated'
import { PolicyBrowserEvents, useRefreshQueriesOn } from '@/lib/browser-events'
import { AGENT_PERMISSIONS, PERMISSION_KEYS } from '@/lib/permissions'
import { usePermissionsAt } from '@/lib/use-permissions'
import type { PolicyRowIdentity } from '@/modules/hierarchy'
import { preferredCounterPolicyApi, PREFERRED_COUNTER_POLICY_TYPE, type PreferredCounterPolicy } from '../../api/preferred-counter-policy-api'
import { eventRulePoliciesApi, PRINTER_EVENT_RULE_POLICY_TYPE, type PrinterEventRulePolicy } from '../../api/event-rule-policies-api'

export const printerPolicyDefinitionKeys = {
  all: (entityId: string) => ['printer-policy-definition', entityId],
}

export type PrinterPolicyDefinition =
  | { valueType: typeof PREFERRED_COUNTER_POLICY_TYPE; policy: PreferredCounterPolicy }
  | { valueType: typeof PRINTER_EVENT_RULE_POLICY_TYPE; policy: PrinterEventRulePolicy }

export function usePrinterPolicyDefinition(entityId: string, policyId: string | undefined) {
  const optionsFor = usePrinterPolicyDefinitionOptions(entityId)
  useRefreshQueriesOn(PolicyBrowserEvents.for({ entityId }).changed, [printerPolicyDefinitionKeys.all(entityId)], 100)
  return useQuery(optionsFor(policyId))
}

export function usePrinterPolicyDefinitionOptions(entityId: string) {
  const { can } = usePermissionsAt(entityId)
  const { user, tokenContext } = useAuth()
  const canReadCounters = can(AGENT_PERMISSIONS.read)
  const canReadEvents = canReadCounters && can(PERMISSION_KEYS.alertRead)
  const canReadActions = canReadEvents && can(PERMISSION_KEYS.alertManage)
  return (policyId: string | undefined) => queryOptions({
    queryKey: [...printerPolicyDefinitionKeys.all(entityId), policyId, user?.userId, user?.impersonation?.actorUserId,
      tokenContext.principal, tokenContext.generation, canReadCounters, canReadEvents, canReadActions],
    enabled: Boolean(policyId) && canReadCounters,
    refetchOnMount: 'always',
    retry: false,
    meta: { assistantEffects: [assistantEffects.settingsChanged] },
    queryFn: async ({ signal }): Promise<PrinterPolicyDefinition> => {
      if (!policyId || !canReadCounters) throw new Error('Policy unavailable')
      try {
        return { valueType: PREFERRED_COUNTER_POLICY_TYPE, policy: await preferredCounterPolicyApi.get(entityId, policyId, signal) }
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 404 || !canReadEvents) throw error
      }
      return { valueType: PRINTER_EVENT_RULE_POLICY_TYPE, policy: await eventRulePoliciesApi.get(entityId, policyId, signal) }
    },
  })
}

export function printerPolicySummary(definition: PrinterPolicyDefinition): PolicyRowIdentity {
  const { policy } = definition
  return {
    id: 'id' in policy ? policy.id : policy.policyId,
    name: policy.name,
    valueType: definition.valueType,
    priority: policy.priority,
    selectorExpression: policy.selectorExpression,
    selectorGui: policy.selectorGui,
  }
}
